import { create } from 'zustand';
import { AppUpdater } from '../native/appUpdater';

export type InstallPhase = 'idle' | 'permission' | 'downloading' | 'installing' | 'downloaded' | 'error';

export interface InstallState {
  phase: InstallPhase;
  /** 0 to 1 while downloading. */
  progress: number;
  /** Downloads the APK inside the app and opens the package installer. */
  start(url: string): Promise<void>;
  /** The file is here already: straight to the installer. */
  install(): Promise<void>;
  /** Back in the app after the installer or the permission screen: ready for another go. */
  resume(): void;
}

export const useInstall = create<InstallState>((set, get) => ({
  phase: 'idle',
  progress: 0,

  async start(url) {
    const { phase } = get();
    if (phase === 'downloading' || phase === 'installing') return;
    try {
      const { allowed } = await AppUpdater.canInstall();
      if (!allowed) {
        set({ phase: 'permission' });
        await AppUpdater.requestInstallPermission();
        return;
      }
      set({ phase: 'downloading', progress: 0 });
      const handle = await AppUpdater.addListener('downloadProgress', ({ received, total }) => {
        set({ progress: total > 0 ? Math.min(1, received / total) : 0 });
      });
      try {
        await AppUpdater.downloadAndInstall({ url });
      } finally {
        await handle.remove();
      }
      set({ phase: 'installing', progress: 1 });
    } catch (e) {
      console.error('update: download or install failed', e);
      set({ phase: 'error' });
    }
  },

  async install() {
    if (get().phase === 'installing') return;
    try {
      set({ phase: 'installing', progress: 1 });
      await AppUpdater.installDownloaded();
    } catch (e) {
      console.error('update: install failed', e);
      set({ phase: 'error' });
    }
  },

  resume() {
    const { phase } = get();
    // Back from the installer without a new version running: the file is still there, so offer it again rather than a fresh download.
    if (phase === 'installing') set({ phase: 'downloaded', progress: 1 });
    else if (phase === 'permission') set({ phase: 'idle', progress: 0 });
  },
}));
